/**
 * SystemPage
 *
 * Organization management (per UX mock). Shows an ORGANIZATION card with
 * the currently selected org's name / address / location / time zone and a
 * gear icon that opens an "Organization settings" modal. A stubbed
 * NOTIFICATIONS card sits beside it.
 *
 * The portal does NOT support creating organizations — only viewing and
 * editing the currently-selected org via the gear modal.
 *
 * Modal layout (Figma, May 2026):
 *   ┌────────────────── Organization settings ──────────────────┐
 *   │ Basic information          │  <pane for active section>   │
 *   │  • Organization details ◀  │                              │
 *   │  • Billing and contact info│                              │
 *   │  • Support access          │                              │
 *   │  • Term of service         │                              │
 *   │ Additional contact info  + │                              │
 *   │  • Finance department      │                              │
 *   │  • IT department           │                              │
 *   │                            │            Cancel  [ Save ]  │
 *   └────────────────────────────────────────────────────────────┘
 */
import { useEffect, useMemo, useState } from 'react';
import organizationService from '../services/organizationService';
import { useTenant } from '../context/TenantContext';
import { toast } from '../components/Toast';
import { Country, State } from 'country-state-city';

const ALLOWED_COUNTRY_CODES = ['US', 'IN', 'AU', 'CN', 'CA', 'AE', 'ZA'];
const ALL_COUNTRIES = Country.getAllCountries()
  .filter(c => ALLOWED_COUNTRY_CODES.includes(c.isoCode))
  .map(c => ({ code: c.isoCode, name: c.name }));

const TIMEZONES = [
  'UTC', 'UTC-12', 'UTC-11', 'UTC-10', 'UTC-9', 'UTC-8', 'UTC-7', 'UTC-6',
  'UTC-5', 'UTC-4', 'UTC-3', 'UTC-2', 'UTC-1',
  'UTC+1', 'UTC+2', 'UTC+3', 'UTC+4', 'UTC+5', 'UTC+5:30', 'UTC+6', 'UTC+7',
  'UTC+8', 'UTC+9', 'UTC+10', 'UTC+11', 'UTC+12',
];

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ja', name: 'Japanese' },
];

/** Stubbed notifications for the right-hand card. Replace with real feed later. */
const SAMPLE_NOTIFICATIONS = [
  { id: 1, icon: 'fa-laptop', title: 'Unknown files detected', subtitle: 'V-uxb-1', time: '16:35 20/12/2022' },
  { id: 2, icon: 'fa-cloud', title: 'New version available', subtitle: 'Cloud', time: '16:35 20/12/2022' },
  { id: 3, icon: 'fa-laptop', title: 'Unknown files detected', subtitle: '', time: '16:35 20/12/2022' },
];

/** Side-nav layout for the settings modal.
 *  Additional contact information items are dynamic — driven off
 *  `form.additionalContacts` — and injected at render time. See
 *  `buildSettingsSections` below. */
const BASIC_INFORMATION_SECTION = {
  group: 'Basic information',
  items: [
    { id: 'org-details', label: 'Organization details', icon: 'fa-building' },
    { id: 'billing',     label: 'Billing and contact information', icon: 'fa-file-invoice' },
    { id: 'support',     label: 'Support access',  icon: 'fa-search' },
    { id: 'tos',         label: 'Term of service', icon: 'fa-file-alt' },
  ],
};

const DEFAULT_CONTACT_TITLES = [
  { title: 'Finance department', icon: 'fa-landmark' },
  { title: 'IT department',      icon: 'fa-desktop' },
];

/** Icon shown for user-added contact rows (no dedicated icon in Figma). */
const USER_CONTACT_ICON = 'fa-user';

/** Prefix used to key the sidebar entry for an additional-contact item. */
const CONTACT_SECTION_PREFIX = 'contact:';

function contactSectionId(title) {
  return `${CONTACT_SECTION_PREFIX}${title}`;
}

/** Returns the sidebar sections rendered by the modal for the current form. */
function buildSettingsSections(additionalContacts) {
  const items = (additionalContacts || []).map((c) => {
    const isDefault = DEFAULT_CONTACT_TITLES.some(
      (d) => d.title.toLowerCase() === (c.infoTitle || '').toLowerCase()
    );
    const icon = isDefault
      ? DEFAULT_CONTACT_TITLES.find(
          (d) => d.title.toLowerCase() === c.infoTitle.toLowerCase()
        ).icon
      : USER_CONTACT_ICON;
    return { id: contactSectionId(c.infoTitle), label: c.infoTitle, icon };
  });
  return [
    BASIC_INFORMATION_SECTION,
    { group: 'Additional contact information', addable: true, items },
  ];
}

/** Ensures Finance/IT rows always exist in the form's additionalContacts list. */
function seedDefaultContacts(existing) {
  const list = Array.isArray(existing) ? [...existing] : [];
  DEFAULT_CONTACT_TITLES.forEach((d, idx) => {
    const hit = list.find(
      (c) => (c.infoTitle || '').toLowerCase() === d.title.toLowerCase()
    );
    if (!hit) {
      list.splice(idx, 0, {
        infoTitle: d.title,
        fullName: '',
        jobTitle: '',
        email: '',
        phone: '',
      });
    }
  });
  return list;
}

/** Generates the next unused "New item N" title for the "+" button. */
function nextNewItemTitle(existing) {
  const taken = new Set(
    (existing || []).map((c) => (c.infoTitle || '').toLowerCase())
  );
  let n = 1;
  while (taken.has(`new item ${n}`)) n += 1;
  return `New item ${n}`;
}

function countryCodeFromName(name) {
  if (!name) return '';
  if (name.length === 2) return name.toUpperCase();
  const hit = ALL_COUNTRIES.find((c) => c.name.toLowerCase() === String(name).toLowerCase());
  return hit ? hit.code : '';
}

function buildLocation(org) {
  if (!org) return '—';
  const parts = [org.city, org.state, org.country].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

/* ============================================================================
 * Field helpers
 * ========================================================================== */
function Label({ children, required, tip }) {
  return (
    <label className="os-label">
      <span>{children}{required && <span className="os-req">*</span>}</span>
      {tip && (
        <span className="os-help" title={tip} aria-label={tip}>
          <i className="far fa-question-circle"></i>
        </span>
      )}
    </label>
  );
}

/* ============================================================================
 * Per-section panes
 * ========================================================================== */
function OrgDetailsPane({ form, setField }) {
  return (
    <>
      <div className="os-field">
        <Label required tip="Legal organization name shown across the portal.">
          Organization Name
        </Label>
        <input
          className="os-input"
          placeholder="Walmart"
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
        />
      </div>
      <div className="os-field">
        <Label required tip="Default time zone for reports and schedules.">
          Time zone
        </Label>
        <select
          className="os-input os-select"
          value={form.timezone}
          onChange={(e) => setField('timezone', e.target.value)}
        >
          <option value="">Region name</option>
          {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
        </select>
      </div>
      <div className="os-field">
        <Label required tip="Default UI language for new users.">
          Language
        </Label>
        <select
          className="os-input os-select"
          value={form.language}
          onChange={(e) => setField('language', e.target.value)}
        >
          <option value="">Language</option>
          {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
      </div>
      <div className="os-field">
        <Label>Description</Label>
        <textarea
          className="os-input os-textarea"
          placeholder="Optional"
          rows={4}
          value={form.description}
          onChange={(e) => setField('description', e.target.value)}
        />
      </div>
    </>
  );
}

function BillingPane({ form, setField, states }) {
  return (
    <>
      <div className="os-field">
        <Label required tip="Legal organization name shown on invoices.">
          Organization Name
        </Label>
        <input
          className="os-input"
          placeholder="Name"
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
        />
      </div>

      <div className="os-field">
        <Label>Billing Address (Line 1)</Label>
        <input
          className="os-input"
          placeholder="Address"
          value={form.addressLine1}
          onChange={(e) => setField('addressLine1', e.target.value)}
        />
      </div>
      <div className="os-field">
        <Label>Billing Address (Line 2)</Label>
        <input
          className="os-input"
          placeholder="Address"
          value={form.addressLine2}
          onChange={(e) => setField('addressLine2', e.target.value)}
        />
      </div>

      <div className="os-field">
        <Label>Postal code</Label>
        <input
          className="os-input"
          placeholder="Postal code"
          value={form.zipCode}
          onChange={(e) => setField('zipCode', e.target.value)}
        />
      </div>

      <div className="os-field">
        <Label required>Billing Contact Email</Label>
        <input
          className="os-input"
          type="email"
          placeholder="Email"
          value={form.billingContactEmail}
          onChange={(e) => setField('billingContactEmail', e.target.value)}
        />
      </div>

      <div className="os-field">
        <Label>Support Contact Email</Label>
        <input
          className="os-input"
          type="email"
          placeholder="Email"
          value={form.supportContactEmail}
          onChange={(e) => setField('supportContactEmail', e.target.value)}
        />
      </div>

      <div className="os-field">
        <Label required>State</Label>
        {states.length > 0 ? (
          <select
            className="os-input os-select"
            value={form.state}
            onChange={(e) => setField('state', e.target.value)}
          >
            <option value="">Select a state</option>
            {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        ) : (
          <input
            className="os-input"
            placeholder="Select a state"
            value={form.state}
            onChange={(e) => setField('state', e.target.value)}
          />
        )}
      </div>

      <div className="os-field">
        <Label>Country</Label>
        <select
          className="os-input os-select"
          value={form.country}
          onChange={(e) => setField('country', e.target.value)}
        >
          <option value="">Country</option>
          {ALL_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </div>

      <div className="os-field">
        <Label required>Phone number</Label>
        <input
          className="os-input"
          placeholder="e.g. +1 415 555 0132"
          value={form.primaryContactPhone}
          onChange={(e) => setField('primaryContactPhone', e.target.value)}
        />
      </div>

      <div className="os-field">
        <Label>Reply-to email</Label>
        <input
          className="os-input"
          type="email"
          placeholder="Email"
          value={form.replyToEmail}
          onChange={(e) => setField('replyToEmail', e.target.value)}
        />
      </div>
    </>
  );
}

/* ----------------------------------------------------------------------------
 * Support access pane — 4 boolean toggles (rendered as checkboxes per Figma).
 * -------------------------------------------------------------------------- */
const SUPPORT_ACCESS_OPTIONS = [
  { key: 'supportViewAccess', label: 'Support view access' },
  { key: 'supportEditAccess', label: 'Support edit access' },
  { key: 'partnerViewAccess', label: 'Partner view access' },
  { key: 'partnerEditAccess', label: 'Partner edit access' },
];

function SupportAccessPane({ form, setField }) {
  return (
    <div className="os-checkbox-list">
      {SUPPORT_ACCESS_OPTIONS.map((opt) => (
        <label key={opt.key} className="os-checkbox">
          <input
            type="checkbox"
            checked={!!form[opt.key]}
            onChange={(e) => setField(opt.key, e.target.checked)}
          />
          <span className="os-checkbox-box"><i className="fas fa-check"></i></span>
          <span className="os-checkbox-label">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------------------
 * Term of service pane — scrollable agreement text + accept action.
 * If already accepted, shows an info row with the acceptor + date instead of
 * the "Accept and submit" button. When the button is clicked, we hit the
 * dedicated backend endpoint (POST /api/organizations/{orgId}/tos/accept)
 * so the acceptance is persisted immediately and the button stays disabled
 * across sessions.
 * -------------------------------------------------------------------------- */
const TOS_BODY = [
  { h: '1. Acceptance of Terms',
    p: 'By signing this document, you enter into a legally binding agreement. You acknowledge that you have read, understood, and agree to be bound by all terms, conditions, and notices contained or referenced herein.' },
  { h: '2. User Obligations',
    p: 'The user agrees to provide true, accurate, and current information. Any attempt to provide false identification or use unauthorized credentials will result in immediate termination of access and potential legal action.' },
  { h: '3. Data Privacy & Audit Logs',
    p: 'We maintain a permanent, immutable record of this agreement. This includes your name, the stated reason for access, and the timestamp of acceptance. This audit log may be reviewed at any time.' },
];

/** Formats an ISO instant / display string as "MM.DD.YYYY". */
function formatTosDate(value) {
  if (!value) return '';
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}.${dd}.${d.getFullYear()}`;
}

function TermsOfServicePane({ form, onAccept, accepting }) {
  const accepted = !!form.tosAccepted;
  return (
    <>
      <div className="os-tos-title">
        <span>TERM OF SERVICE</span>
        <i className="far fa-question-circle os-help"></i>
      </div>
      <div className="os-tos-body">
        {TOS_BODY.map((s) => (
          <div key={s.h} className="os-tos-section">
            <h5>{s.h}</h5>
            <p>{s.p}</p>
          </div>
        ))}
      </div>
      {accepted ? (
        <div className="os-tos-accepted">
          <i className="fas fa-info-circle"></i>
          <span>
            Term of service agreement is accepted by {form.tosAcceptedBy || 'user'}
            {' '}on {formatTosDate(form.tosAcceptedAt)}.
          </span>
        </div>
      ) : (
        <div className="os-tos-actions">
          <button
            type="button"
            className="btn-primary-pill"
            onClick={onAccept}
            disabled={accepting}
          >
            {accepting ? 'Submitting…' : 'Accept and submit'}
          </button>
        </div>
      )}
    </>
  );
}

/* ----------------------------------------------------------------------------
 * Additional contact information pane — used for Finance department,
 * IT department, and every user-added item. Backed by the row in
 * `form.additionalContacts` whose `infoTitle` matches `titleKey`.
 * -------------------------------------------------------------------------- */
function ContactInfoPane({ titleKey, form, setContactField, renameContact, removeContact, isDefault }) {
  const contact = (form.additionalContacts || []).find(
    (c) => c.infoTitle === titleKey
  ) || { infoTitle: titleKey };

  return (
    <>
      <div className="os-field">
        <Label>Information title</Label>
        <input
          className="os-input"
          value={contact.infoTitle || ''}
          disabled={isDefault}
          onChange={(e) => renameContact(titleKey, e.target.value)}
          placeholder="e.g. Finance department"
        />
      </div>
      <div className="os-field">
        <Label>Full name</Label>
        <input
          className="os-input"
          value={contact.fullName || ''}
          onChange={(e) => setContactField(titleKey, 'fullName', e.target.value)}
        />
      </div>
      <div className="os-field">
        <Label>Job title</Label>
        <input
          className="os-input"
          value={contact.jobTitle || ''}
          onChange={(e) => setContactField(titleKey, 'jobTitle', e.target.value)}
        />
      </div>
      <div className="os-field">
        <Label>Email</Label>
        <input
          className="os-input"
          type="email"
          value={contact.email || ''}
          onChange={(e) => setContactField(titleKey, 'email', e.target.value)}
        />
      </div>
      <div className="os-field">
        <Label>Phone number</Label>
        <input
          className="os-input"
          placeholder="e.g. +1 415 555 0132"
          value={contact.phone || ''}
          onChange={(e) => setContactField(titleKey, 'phone', e.target.value)}
        />
      </div>
      {!isDefault && (
        <div className="os-field">
          <button
            type="button"
            className="btn-ghost-small"
            onClick={() => removeContact(titleKey)}
          >
            <i className="fas fa-trash"></i> Remove contact
          </button>
        </div>
      )}
    </>
  );
}

function PlaceholderPane({ title, description }) {
  return (
    <div className="os-empty">
      <i className="fas fa-cog os-empty-icon"></i>
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
  );
}

/* ============================================================================
 * Settings modal
 * ========================================================================== */
function OrgSettingsModal({ open, org, onSave, onClose, saving, onAcceptTos, accepting }) {
  const [active, setActive] = useState('org-details');
  const [form, setForm] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) return;
    setActive('org-details');
    setForm({
      name: org?.name || '',
      displayName: org?.displayName || '',
      description: org?.description || '',
      addressLine1: org?.addressLine1 || '',
      addressLine2: org?.addressLine2 || '',
      city: org?.city || '',
      state: org?.state || '',
      zipCode: org?.zipCode || '',
      country: countryCodeFromName(org?.country) || '',
      timezone: org?.timezone || '',
      language: org?.language || '',
      replyToEmail: org?.replyToEmail || '',
      primaryContactName: org?.primaryContactName || '',
      primaryContactEmail: org?.primaryContactEmail || '',
      primaryContactPhone: org?.primaryContactPhone || '',
      // Billing-specific contacts
      billingContactEmail: org?.billingContactEmail || '',
      supportContactEmail: org?.supportContactEmail || '',
      // Support access toggles
      supportViewAccess: !!org?.supportViewAccess,
      supportEditAccess: !!org?.supportEditAccess,
      partnerViewAccess: !!org?.partnerViewAccess,
      partnerEditAccess: !!org?.partnerEditAccess,
      // Term of service
      tosAccepted:   !!org?.tosAccepted,
      tosAcceptedBy: org?.tosAcceptedBy || '',
      tosAcceptedAt: org?.tosAcceptedAt || '',
      // Additional contact information — Finance / IT / user-added
      additionalContacts: seedDefaultContacts(org?.additionalContacts),
    });
    setErrors({});
  }, [open, org]);

  const setField = (key, val) => setForm((f) => {
    if (key === 'country') return { ...f, country: val, state: '' };
    return { ...f, [key]: val };
  });

  /* -------------------------------------------------------------------------
   * Additional contact list mutators. All are keyed by `infoTitle` (unique
   * per org). Renaming, removing, and adding update the sidebar
   * automatically because `buildSettingsSections(form.additionalContacts)`
   * is derived state.
   * ----------------------------------------------------------------------- */
  const setContactField = (titleKey, field, value) => {
    setForm((f) => ({
      ...f,
      additionalContacts: (f.additionalContacts || []).map((c) =>
        c.infoTitle === titleKey ? { ...c, [field]: value } : c
      ),
    }));
  };

  const renameContact = (titleKey, newTitle) => {
    setForm((f) => {
      const nextTitle = newTitle;
      const list = (f.additionalContacts || []).map((c) =>
        c.infoTitle === titleKey ? { ...c, infoTitle: nextTitle } : c
      );
      return { ...f, additionalContacts: list };
    });
    // Keep the sidebar selection aligned with the renamed contact.
    if (active === contactSectionId(titleKey)) {
      setActive(contactSectionId(newTitle));
    }
  };

  const removeContact = (titleKey) => {
    setForm((f) => ({
      ...f,
      additionalContacts: (f.additionalContacts || []).filter(
        (c) => c.infoTitle !== titleKey
      ),
    }));
    setActive('org-details');
  };

  const addContact = () => {
    setForm((f) => {
      const title = nextNewItemTitle(f.additionalContacts);
      const next = [
        ...(f.additionalContacts || []),
        { infoTitle: title, fullName: '', jobTitle: '', email: '', phone: '' },
      ];
      // Jump to the new item after the state update settles.
      setTimeout(() => setActive(contactSectionId(title)), 0);
      return { ...f, additionalContacts: next };
    });
  };

  const states = useMemo(() => {
    if (!form.country) return [];
    return State.getStatesOfCountry(form.country).map(s => ({ code: s.isoCode, name: s.name }));
  }, [form.country]);

  const sections = useMemo(
    () => buildSettingsSections(form.additionalContacts),
    [form.additionalContacts]
  );

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name?.trim()) next.name = 'Organization name is required';
    // Validate that no two contact rows share a title (backend enforces this too).
    const titles = (form.additionalContacts || []).map(
      (c) => (c.infoTitle || '').trim().toLowerCase()
    );
    const dup = titles.find((t, i) => t && titles.indexOf(t) !== i);
    if (dup) next.additionalContacts = `Duplicate contact title: "${dup}"`;
    const blank = (form.additionalContacts || []).find(
      (c) => !(c.infoTitle || '').trim()
    );
    if (blank) next.additionalContacts = 'Every additional contact needs an "Information title".';
    setErrors(next);
    if (Object.keys(next).length > 0) {
      if (next.name) setActive('org-details');
      return;
    }
    // PATCH-like semantics: the backend COALESCE-updates any null/absent
    // field. ToS-acceptance flags are handled by a dedicated endpoint
    // (POST /tos/accept), so we intentionally skip sending them here to
    // avoid inadvertently clearing them.
    const payload = { ...form };
    delete payload.tosAccepted;
    delete payload.tosAcceptedBy;
    delete payload.tosAcceptedAt;
    // Strip fully-empty contact rows to keep the payload small.
    payload.additionalContacts = (form.additionalContacts || []).filter(
      (c) =>
        (c.infoTitle || '').trim() &&
        (c.fullName || c.jobTitle || c.email || c.phone || c.infoTitle)
    );
    onSave(payload);
  };

  const renderPane = () => {
    if (active.startsWith(CONTACT_SECTION_PREFIX)) {
      const titleKey = active.slice(CONTACT_SECTION_PREFIX.length);
      const isDefault = DEFAULT_CONTACT_TITLES.some(
        (d) => d.title.toLowerCase() === titleKey.toLowerCase()
      );
      return (
        <ContactInfoPane
          titleKey={titleKey}
          form={form}
          setContactField={setContactField}
          renameContact={renameContact}
          removeContact={removeContact}
          isDefault={isDefault}
        />
      );
    }
    switch (active) {
      case 'org-details':
        return <OrgDetailsPane form={form} setField={setField} />;
      case 'billing':
        return <BillingPane form={form} setField={setField} states={states} />;
      case 'support':
        return <SupportAccessPane form={form} setField={setField} />;
      case 'tos':
        return (
          <TermsOfServicePane
            form={form}
            accepting={accepting}
            onAccept={async () => {
              const updated = await onAcceptTos();
              if (updated) {
                setForm((f) => ({
                  ...f,
                  tosAccepted:   !!updated.tosAccepted,
                  tosAcceptedBy: updated.tosAcceptedBy || f.tosAcceptedBy,
                  tosAcceptedAt: updated.tosAcceptedAt || f.tosAcceptedAt,
                }));
              }
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="os-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Organization settings"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="os-modal" onClick={(e) => e.stopPropagation()}>
        <header className="os-modal-header">
          <h2>Organization settings</h2>
        </header>

        <form className="os-modal-body" onSubmit={handleSubmit}>
          <aside className="os-side">
            {sections.map((group) => (
              <div key={group.group} className="os-side-group">
                <div className="os-side-group-header">
                  <span className="os-side-group-title">{group.group}</span>
                  {group.addable && (
                    <button
                      type="button"
                      className="os-side-add"
                      title="Add contact"
                      aria-label="Add contact section"
                      onClick={addContact}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  )}
                </div>
                {group.items.map((it) => (
                  <button
                    key={it.id}
                    type="button"
                    className={`os-side-item ${active === it.id ? 'active' : ''}`}
                    onClick={() => setActive(it.id)}
                  >
                    <i className={`fas ${it.icon}`}></i>
                    <span>{it.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </aside>

          <section className="os-pane">
            {renderPane()}
            {errors.name && active === 'org-details' && (
              <div className="os-error">{errors.name}</div>
            )}
            {errors.additionalContacts && (
              <div className="os-error">{errors.additionalContacts}</div>
            )}
          </section>
        </form>

        <footer className="os-modal-footer">
          <button type="button" className="btn-ghost-small" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary-pill"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
}

export default function SystemPage() {
  const { orgId, currentOrg } = useTenant();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [notifications, setNotifications] = useState(SAMPLE_NOTIFICATIONS);

  const load = async () => {
    if (!orgId) {
      setOrg(null);
      return;
    }
    setLoading(true);
    try {
      const data = await organizationService.getOrganization(orgId);
      setOrg(data);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to load organization');
      setOrg(currentOrg || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const location = useMemo(() => buildLocation(org), [org]);

  const handleSave = async (payload) => {
    if (!orgId) return;
    setSaving(true);
    try {
      const updated = await organizationService.updateOrganization(orgId, payload);
      setOrg((prev) => ({ ...prev, ...payload, ...updated }));
      toast.success('Organization updated');
      setModalOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update organization');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handler for the "Accept and submit" action on the Term of service tab.
   * Persists the acceptance via the dedicated backend endpoint so the button
   * stays disabled on future logins. Returns the updated org so the modal
   * can refresh its local form state without waiting for a full re-fetch.
   */
  const handleAcceptTos = async () => {
    if (!orgId) return null;
    setAccepting(true);
    try {
      const updated = await organizationService.acceptTermsOfService(orgId);
      setOrg((prev) => ({ ...prev, ...updated }));
      toast.success('Terms of service accepted');
      return updated;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        // Someone else beat us to it — re-sync from the server so the UI
        // shows the correct acceptor/date and locks the button.
        toast.info('Terms of service already accepted');
        try {
          const fresh = await organizationService.getOrganization(orgId);
          setOrg(fresh);
          return fresh;
        } catch { /* fall through */ }
      } else {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to accept terms of service');
      }
      return null;
    } finally {
      setAccepting(false);
    }
  };

  return (
    <>
      <div className="page-heading">
        <h1>System</h1>
        <p className="page-subtitle">Organization management</p>
      </div>

      <div className="system-grid">
        {/* Organization card */}
        <section className="info-card">
          <header className="info-card-header">
            <div className="info-card-title">
              <span className="info-card-icon"><i className="fas fa-building"></i></span>
              <span className="info-card-label">ORGANIZATION</span>
            </div>
            <button
              className="info-card-action"
              title="Edit organization"
              onClick={() => orgId && setModalOpen(true)}
              disabled={!orgId || loading}
            >
              <i className="fas fa-cog"></i>
            </button>
          </header>
          <div className="info-card-body">
            {!orgId ? (
              <div className="empty-state" style={{ padding: '24px 0' }}>
                <div>Select an organization from the top-right picker.</div>
              </div>
            ) : loading ? (
              <div className="empty-state" style={{ padding: '24px 0' }}>
                <i className="fas fa-spinner fa-spin"></i>
                <div>Loading…</div>
              </div>
            ) : (
              <div className="info-grid">
                <div className="info-field">
                  <div className="info-field-label">Name</div>
                  <div className="info-field-value">{org?.name || org?.displayName || '—'}</div>
                </div>
                <div className="info-field">
                  <div className="info-field-label">Address</div>
                  <div className="info-field-value">{org?.addressLine1 || '—'}</div>
                </div>
                <div className="info-field">
                  <div className="info-field-label">Location</div>
                  <div className="info-field-value">{location}</div>
                </div>
                <div className="info-field">
                  <div className="info-field-label">Time zone</div>
                  <div className="info-field-value">{org?.timezone || '—'}</div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Notifications card */}
        <section className="info-card">
          <header className="info-card-header">
            <div className="info-card-title">
              <span className="info-card-icon"><i className="far fa-bell"></i></span>
              <span className="info-card-label">NOTIFICATIONS</span>
            </div>
            <button
              className="btn-ghost-small"
              onClick={() => setNotifications([])}
              disabled={notifications.length === 0}
            >
              Clear all
            </button>
          </header>
          <div className="info-card-body notifications-list">
            {notifications.length === 0 ? (
              <div className="empty-state" style={{ padding: '24px 0' }}>
                <div>No notifications</div>
              </div>
            ) : (
              notifications.map((n) => (
                <div key={n.id} className="notification-row">
                  <span className="notification-icon"><i className={`fas ${n.icon}`}></i></span>
                  <div className="notification-text">
                    <div className="notification-title">{n.title}</div>
                    {n.subtitle && <div className="notification-subtitle">{n.subtitle}</div>}
                  </div>
                  <div className="notification-time">{n.time}</div>
                </div>
              ))
            )}
          </div>
        </section>
      </div>

      <OrgSettingsModal
        open={modalOpen}
        org={org}
        saving={saving}
        accepting={accepting}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        onAcceptTos={handleAcceptTos}
      />
    </>
  );
}
